import React, { useState, useEffect } from "react";
import {
   Tearsheet,
   Button,
   useI18nContext,
   Form,
   Spinner,
   Page,
   H1,
   Card,
   Box,
   Flex,
   FlexList,
   Required,
} from "@procore/core-react";
import { Plus } from "@procore/core-icons";
import * as yup from "yup";
import { getOrderedTimeOptions } from "./helpers";
import { getTimeOffReasons, timeOptions } from "./constants";
import type { FormikProps } from "formik";
import { isAfter } from "date-fns";
import {
   type CreateTimeOffFormValues,
   type CreateTimeOffModalProps,
} from "../../time-off-list/time-off-list-prop-types";
import { ErrorBanner } from "../../error-banner";

import { DEFAULT_END_TIME, DEFAULT_START_TIME } from "@/react/shared/constants";
import { useToastAlertContext } from "@procore/toast-alert";
import {
   getTimeOffRepeatOptions,
   getTimeOffTypeOptions,
   getTimeOffWeekendOptions,
} from "../../time-off-list/time-off-list-utils";

export const CreateTimeOffTearsheet = (props: CreateTimeOffModalProps) => {
   const {
      createTimeOff,
      afterCreate,
      streamPeople,
      groupId,
      isOpen,
      personId,
      refetchTimeOffData,
   } = props;
   const I18n = useI18nContext();
   const [open, setOpen] = useState(isOpen ?? false);
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState(false);
   const [people, setPeople] = useState([]);
   const { showToast } = useToastAlertContext();
   const [otherReasonDisplay, setOtherReasonDisplay] = useState(false);

   const initialValues = {
      type: {
         id: 1,
         value: "paid",
         label: I18n.t("views.company.workforce_planning.time_off.paid"),
      },
      repeat: {
         id: 1,
         value: "never",
         label: I18n.t("views.company.workforce_planning.time_off.never"),
      },
      daily_start_time: timeOptions.find((x) => x.value == DEFAULT_START_TIME),
      daily_end_time: timeOptions.find((x) => x.value == DEFAULT_END_TIME),
      cadence: undefined,
      resource_id:
         people.length > 0 ? people.find((person: { id: string }) => person.id === personId) : {},
   };

   const fetchPeople = async () => {
      const people = await streamPeople(groupId);
      setPeople(people);
      setLoading(false);
   };

   useEffect(() => {
      if (open) {
         setLoading(true);
         fetchPeople();
      }
   }, [open]);

   const orderedTimeOptions = getOrderedTimeOptions(DEFAULT_START_TIME);

   /* istanbul ignore next */
   const handleSubmit = async (values: CreateTimeOffFormValues) => {
      setLoading(true);
      try {
         await createTimeOff(values);
         setLoading(false);
         setOpen(false);
         showToast.success(
            I18n.t("views.company.workforce_planning.time_off.modals.success_toast"),
         );
         // refetchTimeOffData is only defined when tearsheet is getting opened from the time off tab in people details
         refetchTimeOffData ? refetchTimeOffData() : afterCreate();
      } catch (err) {
         setLoading(false);
         setError(true);
      }
   };

   return (
      <>
         <Button
            onClick={() => setOpen(true)}
            icon={<Plus />}
            variant={refetchTimeOffData ? "secondary" : undefined}
         >
            {refetchTimeOffData
               ? I18n.t("views.company.workforce_planning.people_details_timeoff.add_time_off")
               : I18n.t("views.company.workforce_planning.create")}
         </Button>
         <Tearsheet
            open={open}
            onClose={() => {
               setOpen(false);
               setOtherReasonDisplay(false);
            }}
            data-testid="create-time-off-tearsheet-id"
         >
            <Spinner loading={loading}>
               <Page>
                  <Page.Main
                     style={{
                        width: refetchTimeOffData ? "952px" : "450px",
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                     }}
                  >
                     <Page.Header>
                        {error && (
                           <Page.Banner>
                              <ErrorBanner
                                 title={I18n.t("views.company.workforce_planning.error")}
                                 content={I18n.t(
                                    "views.company.workforce_planning.time_off.modals.create_error",
                                 )}
                              />
                           </Page.Banner>
                        )}
                        <Page.Title>
                           <H1>
                              {I18n.t(
                                 "views.company.workforce_planning.time_off.modals.create_header",
                              )}
                           </H1>
                        </Page.Title>
                     </Page.Header>
                     {people.length > 0 && (
                        <Form
                           view="create"
                           onSubmit={handleSubmit}
                           initialValues={initialValues as any}
                           validationSchema={yup.object().shape({
                              resource_id: yup
                                 .object()
                                 .required(
                                    I18n.t(
                                       "views.company.workforce_planning.validations.required_field",
                                    ),
                                 ),
                              start_date: yup
                                 .date()
                                 .required(
                                    I18n.t(
                                       "views.company.workforce_planning.validations.required_field",
                                    ),
                                 ),
                              end_date: yup
                                 .date()
                                 .required(
                                    I18n.t(
                                       "views.company.workforce_planning.validations.required_field",
                                    ),
                                 )
                                 .test(
                                    "validate-end-date",
                                    I18n.t(
                                       "views.company.workforce_planning.validations.end_date_before_start_date",
                                    ),
                                    function (endDate) {
                                       const { start_date } = this.parent;
                                       if (!start_date || !endDate) return true;
                                       return endDate.getTime() >= start_date.getTime();
                                    },
                                 ),
                              daily_start_time: yup
                                 .object()
                                 .required(
                                    I18n.t(
                                       "views.company.workforce_planning.validations.required_field",
                                    ),
                                 ),
                              daily_end_time: yup
                                 .object()
                                 .required(
                                    I18n.t(
                                       "views.company.workforce_planning.validations.required_field",
                                    ),
                                 ),
                              reason: yup
                                 .object()
                                 .required(
                                    I18n.t(
                                       "views.company.workforce_planning.validations.required_field",
                                    ),
                                 ),
                              type: yup
                                 .object()
                                 .required(
                                    I18n.t(
                                       "views.company.workforce_planning.validations.required_field",
                                    ),
                                 ),
                              repeat: yup
                                 .object()
                                 .required(
                                    I18n.t(
                                       "views.company.workforce_planning.validations.required_field",
                                    ),
                                 ),
                              cadence: yup.number().test(
                                 "validate-cadence",
                                 I18n.t(
                                    "views.company.workforce_planning.validations.required_field",
                                 ),
                                 // If time off does not repeat, cadence can be undefined. Otherwise, cadence must  be set and >= 1
                                 function (cadence) {
                                    const { repeat } = this.parent;
                                    if (repeat.value == "never") {
                                       return true;
                                    } else if (!cadence || cadence < 1) {
                                       return false;
                                    }
                                    return true;
                                 },
                              ),
                              repeat_end_date: yup
                                 .date()
                                 .test(
                                    "validate-repeat-end-day",
                                    I18n.t(
                                       "views.company.workforce_planning.validations.required_field",
                                    ),
                                    // Repeat end date must be after end date if the time off is repeating
                                    function (repeat_end_date) {
                                       const { repeat } = this.parent;
                                       if (repeat.value == "never") {
                                          return true;
                                       } else if (!repeat_end_date) {
                                          return false;
                                       }
                                       return true;
                                    },
                                 )
                                 .test(
                                    "validate-repeat-end-date-after-end-date",
                                    I18n.t(
                                       "views.company.workforce_planning.validations.repeat_end_date_before_end_date",
                                    ),
                                    function (repeat_end_date) {
                                       const { end_date } = this.parent;
                                       if (
                                          repeat_end_date &&
                                          repeat_end_date.getTime() < end_date.getTime()
                                       ) {
                                          return false;
                                       }

                                       return true;
                                    },
                                 ),
                           })}
                           data-testid="create-time-off-tearsheet-form"
                        >
                           {/* @ts-expect-error Core React Form uses Formik under the hood; this is valid implementation */}
                           {(formikProps: FormikProps<any>) => {
                              const { repeat } = formikProps.values;
                              return (
                                 <Form.Form data-testid="create-time-off-tearsheet">
                                    <Page.Body style={{ width: "100%", marginBottom: "auto" }}>
                                       <Card>
                                          <Box padding="md">
                                             <Form.Row>
                                                <Form.Select
                                                   name="resource_id"
                                                   label={I18n.t(
                                                      "views.company.workforce_planning.resource",
                                                   )}
                                                   options={people}
                                                   colStart={1}
                                                   colWidth={11}
                                                   block
                                                   required
                                                   disabled={!!personId}
                                                />
                                             </Form.Row>
                                             <Form.Row>
                                                <Form.DateSelect
                                                   name="start_date"
                                                   label={I18n.t(
                                                      "views.company.workforce_planning.start_date",
                                                   )}
                                                   colStart={1}
                                                   colWidth={6}
                                                   onChange={(date) => {
                                                      // Manually set end date when the user selects a start date if end date is not set.
                                                      // This mimics legacy behavior so new modal does not add clicks.
                                                      formikProps.setFieldValue("start_date", date);
                                                      if (!formikProps.values.end_date && date) {
                                                         formikProps.setFieldValue(
                                                            "end_date",
                                                            date,
                                                         );
                                                      }
                                                   }}
                                                   required
                                                   data-testid="start-date-select"
                                                />
                                                <Form.DateSelect
                                                   name="end_date"
                                                   label={I18n.t(
                                                      "views.company.workforce_planning.end_date",
                                                   )}
                                                   colStart={7}
                                                   colWidth={6}
                                                   disabledDate={(date) => {
                                                      const { start_date } = formikProps.values;

                                                      return isAfter(start_date, date);
                                                   }}
                                                   required
                                                   data-testid="end-date-select"
                                                />
                                             </Form.Row>
                                             <Form.Row>
                                                <Form.Select
                                                   name="daily_start_time"
                                                   label={I18n.t(
                                                      "views.company.workforce_planning.time_off.modals.labels.daily_start_time",
                                                   )}
                                                   options={orderedTimeOptions}
                                                   colStart={1}
                                                   colWidth={5}
                                                   required
                                                   onSearch={false}
                                                />
                                                <Form.Select
                                                   name="daily_end_time"
                                                   label={I18n.t(
                                                      "views.company.workforce_planning.time_off.modals.labels.daily_end_time",
                                                   )}
                                                   options={orderedTimeOptions}
                                                   colStart={7}
                                                   colWidth={5}
                                                   required
                                                   onSearch={false}
                                                />
                                             </Form.Row>
                                             <Form.Row>
                                                <Form.Select
                                                   name="reason"
                                                   label={I18n.t(
                                                      "views.company.workforce_planning.time_off.modals.labels.reason",
                                                   )}
                                                   options={getTimeOffReasons(I18n)}
                                                   required
                                                   onSearch={false}
                                                   colStart={1}
                                                   colWidth={11}
                                                   block
                                                   onSelect={(item) => {
                                                      setOtherReasonDisplay(false);
                                                      if (item.item.value === "other") {
                                                         setOtherReasonDisplay(true);
                                                      }
                                                   }}
                                                   onClear={() => {
                                                      setOtherReasonDisplay(false);
                                                   }}
                                                   data-testid="reason-select"
                                                />
                                             </Form.Row>
                                             {otherReasonDisplay && (
                                                <Form.Row>
                                                   <Form.Text
                                                      name="other_reason"
                                                      label={I18n.t(
                                                         "views.company.workforce_planning.time_off.other_reason",
                                                      )}
                                                      colStart={1}
                                                      colWidth={11}
                                                      data-testid="other-reason-input"
                                                   />
                                                </Form.Row>
                                             )}
                                             <Form.Row>
                                                <Form.RadioButtons
                                                   name="type"
                                                   label={I18n.t(
                                                      "views.company.workforce_planning.time_off.modals.labels.type",
                                                   )}
                                                   options={getTimeOffTypeOptions(I18n)}
                                                />
                                                <Form.Checkboxes
                                                   colStart={7}
                                                   name="weekend"
                                                   label={I18n.t(
                                                      "views.company.workforce_planning.time_off.modals.labels.weekends",
                                                   )}
                                                   options={getTimeOffWeekendOptions(I18n)}
                                                />
                                             </Form.Row>
                                             <Form.Row>
                                                <Form.RadioButtons
                                                   name="repeat"
                                                   label={I18n.t(
                                                      "views.company.workforce_planning.time_off.repeat",
                                                   )}
                                                   options={getTimeOffRepeatOptions(I18n)}
                                                   data-testid="repeat-select"
                                                />
                                                {repeat.value !== "never" && (
                                                   <Form.Number
                                                      colStart={7}
                                                      colWidth={4}
                                                      min={1}
                                                      style={{ height: "36px", width: "145px" }}
                                                      name="cadence"
                                                      label={I18n.t(
                                                         "views.company.workforce_planning.time_off.modals.labels.cadence",
                                                      )}
                                                      required
                                                      onChange={(e) => {
                                                         if (parseInt(e.value) < 1) {
                                                            formikProps.setFieldValue(
                                                               "cadence",
                                                               "",
                                                            );
                                                         }
                                                      }}
                                                   />
                                                )}
                                             </Form.Row>
                                             {repeat.value !== "never" && (
                                                // When showing repeat end day, use  negative top margin to group with cadence
                                                <Form.Row style={{ marginTop: "-90px" }}>
                                                   <Form.DateSelect
                                                      colStart={7}
                                                      colWidth={4}
                                                      name="repeat_end_date"
                                                      label={I18n.t(
                                                         "views.company.workforce_planning.time_off.modals.labels.repeat_end_date",
                                                      )}
                                                      disabledDate={(date) => {
                                                         const { end_date } = formikProps.values;

                                                         return isAfter(end_date, date);
                                                      }}
                                                      required
                                                      data-testid="repeat-end-date-select"
                                                   />
                                                </Form.Row>
                                             )}
                                          </Box>
                                       </Card>
                                    </Page.Body>
                                    <Page.Footer>
                                       <Box padding="md">
                                          <Flex justifyContent="space-between" alignItems="center">
                                             <Required showLabel />
                                             <FlexList space="md">
                                                <Button
                                                   variant="tertiary"
                                                   onClick={() => {
                                                      setOpen(false);
                                                      setOtherReasonDisplay(false);
                                                   }}
                                                >
                                                   {I18n.t(
                                                      "views.company.workforce_planning.cancel",
                                                   )}
                                                </Button>
                                                <Button variant="primary" type="submit">
                                                   {I18n.t("views.company.workforce_planning.save")}
                                                </Button>
                                             </FlexList>
                                          </Flex>
                                       </Box>
                                    </Page.Footer>
                                 </Form.Form>
                              );
                           }}
                        </Form>
                     )}
                  </Page.Main>
               </Page>
            </Spinner>
         </Tearsheet>
      </>
   );
};
